import { createContext, useContext, useEffect, useSyncExternalStore, type ReactNode } from "react";
import { useAuth } from "./AuthContext";
import { isAdmin } from "../lib/access";
import {
  getUiLoadState,
  isUiLoadReduced,
  setUiLoadAdminExempt,
  setUiLoadFromServer,
  subscribeUiLoad,
  type UiLoadState,
} from "../lib/uiLoadStore";

type UiLoadContextValue = UiLoadState & {
  blocked: boolean;
  loadReduced: boolean;
};

const UiLoadContext = createContext<UiLoadContextValue | null>(null);

function useUiLoadSnapshot(): UiLoadContextValue {
  const snapshot = useSyncExternalStore(subscribeUiLoad, getUiLoadState, getUiLoadState);
  const blocked = snapshot.paused && !snapshot.adminExempt;
  return {
    ...snapshot,
    blocked,
    loadReduced: isUiLoadReduced(),
  };
}

export function UiLoadProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const uiLoad = useUiLoadSnapshot();

  useEffect(() => {
    setUiLoadAdminExempt(isAdmin(user));
  }, [user]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch("/api/ui-load", { credentials: "same-origin", cache: "no-store" });
        if (!res.ok || cancelled) return;
        const data = await res.json();
        setUiLoadFromServer({
          paused: data.paused,
          message: data.message,
          sinceMs: data.since_ms,
          byLogin: data.by_login,
        });
      } catch {
        /* WS will push updates when connected */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!uiLoad.blocked) return;
    let cancelled = false;
    const poll = async () => {
      try {
        const res = await fetch("/api/ui-load", { credentials: "same-origin", cache: "no-store" });
        if (!res.ok || cancelled) return;
        const data = await res.json();
        setUiLoadFromServer({
          paused: data.paused,
          message: data.message,
          sinceMs: data.since_ms,
          byLogin: data.by_login,
        });
      } catch {
        /* retry on next interval */
      }
    };
    const id = window.setInterval(() => void poll(), 10_000);
    return () => {
      cancelled = true;
      window.clearInterval(id);
    };
  }, [uiLoad.blocked]);

  return (
    <UiLoadContext.Provider value={uiLoad}>
      {children}
      {uiLoad.blocked && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 p-6"
          role="alertdialog"
          aria-modal="true"
          aria-labelledby="ui-load-pause-title"
        >
          <div className="max-w-md rounded-lg border border-border-subtle bg-surface-primary p-6 shadow-xl">
            <h2
              id="ui-load-pause-title"
              className="mb-2 font-semibold text-text-primary"
              style={{ fontSize: "var(--font-heading)" }}
            >
              Dashboard paused
            </h2>
            <p className="text-text-secondary" style={{ fontSize: "var(--font-body)" }}>
              {uiLoad.message}
            </p>
            {uiLoad.byLogin && (
              <p className="mt-3 text-text-tertiary" style={{ fontSize: "var(--font-micro)" }}>
                Paused by {uiLoad.byLogin}
                {uiLoad.sinceMs
                  ? ` · ${new Date(uiLoad.sinceMs).toLocaleTimeString()}`
                  : ""}
              </p>
            )}
          </div>
        </div>
      )}
    </UiLoadContext.Provider>
  );
}

export function useUiLoad() {
  const ctx = useContext(UiLoadContext);
  if (!ctx) throw new Error("useUiLoad must be used within UiLoadProvider");
  return ctx;
}

export function useUiLoadReduced(): boolean {
  const ctx = useContext(UiLoadContext);
  if (ctx) return ctx.loadReduced;
  return isUiLoadReduced();
}
